import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Button,
  Badge,
} from "reactstrap";
import { REGEX, MAX_FILE_SIZE } from "constants/CommonConstants";
import AdminHeader from "components/Headers/AdminHeader";
import { useLocation, useNavigate } from "react-router-dom";
import Avatar from "components/CommonComponents/Avatar";
import { maskEmail } from "components/CommonComponents/CommonFunctions";

export const initialState = {
  organizationName: {
    value: "ABC Pvt Ltd",
    error: "",
  },
  contactPersonName: {
    value: "John Doe",
    error: "",
  },
  email: {
    value: "johndoe@example.com",
    error: "",
  },
  mobileNumber: {
    value: "9876543210",
    error: "",
  },
  alternateMobileNumber: {
    value: "9123456780",
    error: "",
  },
  gstNumber: {
    value: "22AAAAA0000A1Z5",
    error: "",
  },
  registeredAddress: {
    value: "123, Business Street, City",
    error: "",
  },
  panNumber: {
    value: "AAACH2702H",
    error: "",
  },
  state: {
    value: "Maharashtra",
    error: "",
  },
  city: {
    value: "Nashik",
    error: "",
  },
  pincode: {
    value: "422001",
    error: "",
  },
  companyLogo: {
    value: null,
    error: "",
  },
  supportingDocuments: {
    value: [],
    error: "",
  },
  role: {
    value: "Channel Partner",
    error: "",
  },
  accountStatus: {
    value: "ACTIVE",
    error: "",
  },
  createdOn: {
    value: "10-Dec-2024",
    error: "",
  },
};

function ChannelPartnerProfile({
  channelPartnerId,
  Edit = false,
  isAdminView = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(isAdminView ? Edit : false);
  // we have controlled if edit?=true from url for ADMIN role if edit = indefined/false then view mode
  const [channelPartnerData, setChannelPartnerData] = useState(initialState);

  useEffect(() => {
    // we can check id coming from InstructorDetails File
    if (channelPartnerId) {
      //Api call to fecth the details
    }
  }, [channelPartnerId]);

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "organizationName":
        return !trimmedValue
          ? "Please enter organization name"
          : !REGEX.ALPHABETS_NUM_SPACE_SPECIAL_SYMBOLS_ALLOWED.test(
              trimmedValue
            )
          ? "Organization name contains invalid characters"
          : "";

      case "contactPersonName":
        return !trimmedValue
          ? "Please enter contact person name"
          : !REGEX.ALPHABETS_NUM_SPACE_SPECIAL_SYMBOLS_ALLOWED.test(
              trimmedValue
            )
          ? "Contact person name must contain only letters and spaces"
          : "";

      case "email":
        return !trimmedValue
          ? "Please enter email"
          : !REGEX.EMAIL_REGEX.test(trimmedValue)
          ? "Please enter a valid email"
          : "";

      case "mobileNumber":
        return !value
          ? "Please enter mobile number"
          : value.length !== 10
          ? "Mobile number must be 10 digits"
          : !REGEX.PHONE_NO_REGEX.test(value)
          ? "Mobile number must contain only digits"
          : "";

      case "gstNumber":
        return !trimmedValue
          ? "Please enter GST number"
          : !REGEX.GST_REGEX.test(trimmedValue)
          ? "Invalid GST number"
          : "";

      case "registeredAddress":
        return !trimmedValue
          ? "Please enter registered address"
          : trimmedValue.length > 300
          ? "Address cannot exceed 300 characters"
          : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newChannelPartner = { ...channelPartnerData };
    Object.keys(newChannelPartner).forEach((key) => {
      newChannelPartner[key] = {
        ...newChannelPartner[key],
        error: getValidationError(key, newChannelPartner[key].value),
      };
    });
    setChannelPartnerData(newChannelPartner);
    return Object.values(newChannelPartner).every((item) => item.error === "");
  };

  const handleSave = () => {
    if (!validateForm()) return;
    // after success
    setIsEditing(false);
    if (isAdminView) {
      const params = new URLSearchParams(location.search);
      params.delete("edit");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    navigate("/admin/channel-partner-list");
  };

  const handleOnCancel = () => {
    setChannelPartnerData(initialState);
    setIsEditing(false);

    if (isAdminView) {
      const params = new URLSearchParams(location.search);
      params.delete("edit");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  };

  const removeDocument = (index) => {
    setChannelPartnerData((prev) => ({
      ...prev,
      supportingDocuments: {
        value: prev.supportingDocuments.value.filter((_, i) => i !== index),
        error: "",
      },
    }));
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setChannelPartnerData((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: trimmedValue, error },
    }));
  };

  const handleSupportingDocsChange = (e) => {
    const files = Array.from(e.target.files);

    const newFiles = files
      .filter(
        (file) =>
          file.type.startsWith("image/") || file.type === "application/pdf"
      )
      .map((file) => ({
        file,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
        name: file.name,
        type: file.type,
      }));

    setChannelPartnerData((prev) => ({
      ...prev,
      supportingDocuments: {
        value: [...prev.supportingDocuments.value, ...newFiles].slice(0, 5),
        error: "",
      },
    }));

    e.target.value = "";
  };

  const handleImageChange = (file) => {
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setChannelPartnerData((prev) => ({
        ...prev,
        companyLogo: {
          value: null,
          error: "Image should not exceed max size of 2 MB",
        },
      }));
      return;
    }

    setChannelPartnerData((prev) => ({
      ...prev,
      companyLogo: {
        value: {
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
          type: file.type,
        },
        error: "",
      },
    }));
  };

  const handleInputChange = (field, value) => {
    setChannelPartnerData((prev) => {
      let updatedState = {
        ...prev,
        [field]: {
          value,
          error: "",
        },
      };
      return updatedState;
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    if (isAdminView) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("edit", "true");

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
    setChannelPartnerData({
      organizationName: {
        value: channelPartnerData.organizationName.value,
        error: "",
      },
      contactPersonName: {
        value: channelPartnerData.contactPersonName.value,
        error: "",
      },
      email: {
        value: channelPartnerData.email.value,
        error: "",
      },
      mobileNumber: {
        value: channelPartnerData.mobileNumber.value,
        error: "",
      },
      alternateMobileNumber: {
        value: channelPartnerData.alternateMobileNumber.value,
        error: "",
      },
      gstNumber: {
        value: channelPartnerData.gstNumber.value,
        error: "",
      },
      registeredAddress: {
        value: channelPartnerData.registeredAddress.value,
        error: "",
      },
      panNumber: {
        value: channelPartnerData.panNumber.value,
        error: "",
      },
      state: {
        value: channelPartnerData.state.value,
        error: "",
      },
      city: {
        value: channelPartnerData.city.value,
        error: "",
      },
      pincode: {
        value: channelPartnerData.pincode.value,
        error: "",
      },
      companyLogo: {
        value: channelPartnerData.companyLogo.value,
        error: "",
      },
      supportingDocuments: {
        value: [...channelPartnerData.supportingDocuments.value],
        error: "",
      },
      role: {
        value: channelPartnerData.role.value,
        error: "",
      },
      accountStatus: {
        value: channelPartnerData.accountStatus.value,
        error: "",
      },
      createdOn: {
        value: channelPartnerData.createdOn.value,
        error: "",
      },
    });
  };

  const handleRemove = (e) => {
    e.stopPropagation();

    setChannelPartnerData((prev) => ({
      ...prev,
      companyLogo: { value: null, error: "" },
    }));
  };

  return (
    <>
      {/* If ADMIN  view by id -channel partner  details if CHANNEL PARTNER - my profile */}
      {isAdminView ? (
        <AdminHeader name="Channel Partner Details" />
      ) : (
        <AdminHeader name="My Profile" />
      )}
      <Container fluid className="mt--6">
        <Row>
          <Col xl="12">
            <Card className="shadow mb-4">
              <CardBody>
                <Row className="align-items-center">
                  <Col md="2" className="text-center">
                    <FormGroup className="profile-photo-container position-relative text-center">
                      {channelPartnerData.companyLogo?.value ? (
                        <img
                          src={channelPartnerData.companyLogo.value.preview}
                          alt="companyLogo"
                          className="profile-photo-preview"
                        />
                      ) : (
                        <Avatar
                          name={channelPartnerData.organizationName?.value}
                        />
                      )}

                      {isEditing && (
                        <>
                          <small className="text-muted d-block mt-2">
                            Accepted formats: PNG, JPG. Maximum file size: 2 MB.
                          </small>

                          <span
                            className="profile-img-edit cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("companyLogoInputTop")
                                .click()
                            }
                          >
                            <i className="fa fa-camera" />
                          </span>

                          <Input
                            type="file"
                            id="companyLogoInputTop"
                            accept="image/*"
                            className="d-none"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              handleImageChange(file);
                              e.target.value = ""; // reset input
                            }}
                          />
                        </>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="7">
                    <h2 className="mb-0">
                      {channelPartnerData.organizationName.value}
                    </h2>
                    <Badge
                      className="px-3 py-2"
                      color={
                        channelPartnerData.accountStatus?.value === "ACTIVE"
                          ? "success"
                          : "warning"
                      }
                      pill
                    >
                      {channelPartnerData.accountStatus?.value === "INACTIVE"
                        ? "Suspended"
                        : channelPartnerData.accountStatus?.value}
                    </Badge>
                  </Col>

                  <Col md="3" className="text-right">
                    {!isEditing ? (
                      <Button
                        className="btn-deep-blue"
                        color="primary"
                        onClick={handleEdit}
                      >
                        <i class="fa-solid fa-pencil mr-1"></i>
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          color="secondary"
                          className="mr-2 px-4 py-2"
                          onClick={handleOnCancel}
                        >
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          className="btn-deep-blue mr-2 px-4 py-2"
                          type="submit"
                          onClick={handleSave}
                        >
                          Save
                        </Button>
                      </>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Basic Info Section */}
            <Card className="shadow mb-4">
              <CardHeader>
                <h3 className="mb-0">Basic Information</h3>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>
                        Organization Name{" "}
                        {isEditing && <span className="text-danger"> *</span>}
                      </Label>
                      {isEditing ? (
                        <>
                          <Input
                            type="text"
                            name="organizationName"
                            value={channelPartnerData.organizationName.value}
                            onChange={(e) =>
                              handleInputChange(
                                "organizationName",
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              channelPartnerData.organizationName.error
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {channelPartnerData.organizationName.error && (
                            <div className="invalid-feedback d-block">
                              {channelPartnerData.organizationName.error}
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="small-text">
                          {channelPartnerData.organizationName.value}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>
                        Contact Person Name
                        {isEditing && <span className="text-danger"> *</span>}
                      </Label>
                      {isEditing ? (
                        <>
                          <Input
                            type="text"
                            name="contactPersonName"
                            value={channelPartnerData.contactPersonName.value}
                            onChange={(e) =>
                              handleInputChange(
                                "contactPersonName",
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              channelPartnerData.contactPersonName.error
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {channelPartnerData.contactPersonName.error && (
                            <div className="invalid-feedback d-block">
                              {channelPartnerData.contactPersonName.error}
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="small-text">
                          {channelPartnerData.contactPersonName.value}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>
                        Email
                        {isEditing && <span className="text-danger"> *</span>}
                      </Label>
                      {isEditing ? (
                        <>
                          <Input
                            name="email"
                            value={channelPartnerData.email.value}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              channelPartnerData.email.error ? "is-invalid" : ""
                            }
                          />
                          {channelPartnerData.email.error && (
                            <div className="invalid-feedback d-block">
                              {channelPartnerData.email.error}
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="small-text">
                          {maskEmail(channelPartnerData.email.value)}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>
                        Mobile Number
                        {isEditing && <span className="text-danger"> *</span>}
                      </Label>
                      {isEditing ? (
                        <>
                          <Input
                            name="mobileNumber"
                            value={channelPartnerData.mobileNumber.value}
                            onChange={(e) =>
                              handleInputChange("mobileNumber", e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              channelPartnerData.mobileNumber.error
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {channelPartnerData.mobileNumber.error && (
                            <div className="invalid-feedback d-block">
                              {channelPartnerData.mobileNumber.error}
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="small-text">
                          {channelPartnerData.mobileNumber.value}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* business details */}
            <Card className="shadow mb-4">
              <CardHeader>
                <h3 className="mb-0">Business & Compliance Details</h3>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>
                        GST Number{" "}
                        {isEditing && <span className="text-danger"> *</span>}
                      </Label>
                      {isEditing ? (
                        <>
                          <Input
                            name="gstNumber"
                            value={channelPartnerData.gstNumber.value}
                            onChange={(e) =>
                              handleInputChange("gstNumber", e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              channelPartnerData.gstNumber.error
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {channelPartnerData.gstNumber.error && (
                            <div className="invalid-feedback d-block">
                              {channelPartnerData.gstNumber.error}
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="small-text">
                          {channelPartnerData.gstNumber.value}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>PAN Number </Label>
                      <small className="small-text">
                        {channelPartnerData.panNumber?.value || "-"}
                      </small>
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>State </Label>
                      <small className="small-text">
                        {channelPartnerData.state?.value || "-"}
                      </small>
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>City </Label>
                      <small className="small-text">
                        {channelPartnerData.city?.value || "-"}
                      </small>
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>Pincode</Label>
                      <small className="small-text">
                        {channelPartnerData.pincode?.value || "-"}
                      </small>
                    </FormGroup>
                  </Col>

                  <Col md="12">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>
                        Registered Address{" "}
                        {isEditing && <span className="text-danger"> *</span>}
                      </Label>
                      {isEditing ? (
                        <>
                          <Input
                            type="textarea"
                            name="registeredAddress"
                            value={channelPartnerData.registeredAddress.value}
                            onChange={(e) =>
                              handleInputChange(
                                "registeredAddress",
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              channelPartnerData.registeredAddress.error
                                ? "is-invalid"
                                : ""
                            }
                            maxLength={300}
                          />
                          {channelPartnerData.registeredAddress.error && (
                            <div className="invalid-feedback d-block">
                              {channelPartnerData.registeredAddress.error}
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="small-text">
                          {channelPartnerData.registeredAddress.value}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* documents details */}
            <Card className="shadow mb-4">
              <CardHeader>
                <h3 className="mb-0">Identity & Documents</h3>
              </CardHeader>
              <CardBody>
                <Row>
                  {/* Company Logo */}
                  <Col md={4} sm={12}>
                    <FormGroup>
                      <Label className="fw-bold mb-2">
                        Company Logo
                        {isEditing && (
                          <span className="text-muted mt-1 d-block">
                            (Accepted formats: PNG / JPG, max 2 MB)
                          </span>
                        )}
                      </Label>

                      {isEditing ? (
                        <div
                          className="w-100 p-3 border rounded text-center cursor-pointer"
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith("image/")) {
                              handleImageChange(file);
                            }
                          }}
                          onClick={() =>
                            document.getElementById("companyLogoInput").click()
                          }
                        >
                          {channelPartnerData.companyLogo?.value ? (
                            <div className=" d-inline-block position-relative">
                              <img
                                src={
                                  channelPartnerData.companyLogo.value.preview
                                }
                                alt={channelPartnerData.companyLogo.value.name}
                                className="media-preview"
                              />
                              <span
                                className="remove-image"
                                onClick={handleRemove}
                              >
                                &times;
                              </span>
                            </div>
                          ) : (
                            <>
                              <i className="fa fa-cloud-upload fa-2x text-muted mb-2" />
                              <p className="mb-1 text-muted">
                                Click or drag logo to upload
                              </p>
                              <span className="btn btn-sm btn-color">
                                Choose Image
                              </span>
                            </>
                          )}

                          <Input
                            type="file"
                            accept="image/*"
                            className="d-none"
                            id="companyLogoInput"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              handleImageChange(file);
                              e.target.value = ""; // reset input
                            }}
                          />
                        </div>
                      ) : (
                        <div className="media-grid">
                          {channelPartnerData.companyLogo?.value ? (
                            <div className="media-card">
                              <div className="media-preview-wrapper">
                                <img
                                  src={
                                    channelPartnerData.companyLogo.value.preview
                                  }
                                  alt={
                                    channelPartnerData.companyLogo.value.name
                                  }
                                  className="media-preview"
                                />
                              </div>
                              <div className="media-info">
                                <div className="media-title text-truncate">
                                  {channelPartnerData.companyLogo.value.name}
                                </div>
                                <div className="media-actions">
                                  <span
                                    className="link-action view-link"
                                    onClick={() =>
                                      window.open(
                                        channelPartnerData.companyLogo.value
                                          .preview,
                                        "_blank"
                                      )
                                    }
                                  >
                                    <i className="fa fa-eye mr-1" /> View
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="border rounded p-3 text-center text-muted w-100">
                              <p className="mb-0">Company logo not uploaded</p>
                            </div>
                          )}
                        </div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Supporting Documents*/}
                  <Col md={8} sm={12}>
                    <FormGroup>
                      <Label className="form-control-label">
                        Supporting Documents
                      </Label>
                      <input
                        type="file"
                        id="supportingDocs"
                        multiple
                        accept="image/*,application/pdf"
                        className="d-none"
                        onChange={handleSupportingDocsChange}
                      />

                      {isEditing && (
                        <span className="text-muted mt-1 d-block">
                          (Accepted formats: PDF, PNG, JPG. Max 5 files)
                        </span>
                      )}
                      {isEditing ? (
                        <div
                          className="product-image-upload w-100 p-3 border rounded text-center cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("supportingDocs").click();
                          }}
                        >
                          {channelPartnerData.supportingDocuments.value.length >
                          0 ? (
                            <div className="d-flex flex-wrap align-items-start">
                              {channelPartnerData.supportingDocuments.value.map(
                                (item, index) => (
                                  <div
                                    key={index}
                                    className="position-relative mr-3 mb-3"
                                    style={{ width: "7rem" }}
                                  >
                                    <div className="preview-wrapper">
                                      {item.type === "application/pdf" ? (
                                        <i className="fa fa-file-pdf text-muted fa-4x" />
                                      ) : (
                                        <img
                                          src={item.preview}
                                          className="product-preview-img"
                                          alt={item.name}
                                        />
                                      )}
                                    </div>

                                    <span
                                      className="remove-image"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeDocument(index);
                                      }}
                                    >
                                      &times;
                                    </span>
                                  </div>
                                )
                              )}

                              {channelPartnerData.supportingDocuments.value
                                .length < 5 && (
                                <div
                                  className="add-more-container"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    document
                                      .getElementById("supportingDocs")
                                      .click();
                                  }}
                                >
                                  <div className="add-more-file-wrapper">+</div>
                                  <span className="small">Add more</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="d-flex flex-column align-items-center">
                              <i className="fa fa-cloud-upload fa-2x text-muted mb-2" />
                              <p className="mb-1 text-muted">
                                Click to upload supporting documents
                              </p>
                              <span className="btn btn-sm btn-color">
                                Choose Files
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="media-grid">
                          {channelPartnerData.supportingDocuments.value.length >
                          0 ? (
                            channelPartnerData.supportingDocuments.value.map(
                              (item, index) => (
                                <div key={index} className="media-card">
                                  <div className="media-preview-wrapper">
                                    {item.type === "application/pdf" ? (
                                      <i className="fa fa-file-pdf fa-3x text-danger" />
                                    ) : (
                                      <img
                                        src={item.preview}
                                        alt={item.name}
                                        className="media-preview"
                                      />
                                    )}
                                  </div>

                                  <div className="media-info">
                                    <div className="media-title text-truncate">
                                      {item.name}
                                    </div>

                                    <div className="media-actions">
                                      <span
                                        className="link-action view-link"
                                        onClick={() =>
                                          window.open(item.preview, "_blank")
                                        }
                                      >
                                        <i className="fa fa-eye mr-1" /> View
                                      </span>

                                      <span
                                        className="link-action download-link"
                                        onClick={() => {
                                          const a = document.createElement("a");
                                          a.href = item.preview;
                                          a.download = item.name;
                                          a.click();
                                        }}
                                      >
                                        <i className="fa fa-download mr-1" />{" "}
                                        Download
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )
                            )
                          ) : (
                            <div className="border rounded p-3 text-center text-muted w-100">
                              <p className="mb-0">
                                No supporting documents uploaded
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* system and access details */}
            <Card className="shadow mb-4">
              <CardHeader>
                <h3 className="mb-0">System & Access Details</h3>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col md="6">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label>Role</Label>
                      <small className="small-text">
                        {channelPartnerData.role?.value || "-"}
                      </small>
                    </FormGroup>
                  </Col>

                  <Col md="6">
                    <FormGroup className="d-flex flex-column align-items-start">
                      <Label className="form-control-label">
                        Account Status
                      </Label>

                      <span>
                        <Badge
                          className="px-3 py-2"
                          color={
                            channelPartnerData.accountStatus.value.toUpperCase() ===
                            "ACTIVE"
                              ? "success"
                              : "warning"
                          }
                          pill
                        >
                          {channelPartnerData.accountStatus.value.toUpperCase() ===
                          "INACTIVE"
                            ? "Suspended"
                            : channelPartnerData.accountStatus.value}
                        </Badge>
                      </span>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ChannelPartnerProfile;
